import React from 'react'

import {
  BodyText,
  MetaText,
  Table,
  StatusText,
  Content,
  Media,
  MediaContainer,
  Label,
  CTALink,
  Image,
} from '@pb/components'
import { formatCurrency } from '@pb/utils'
import cn from 'classnames'

import { APP_DEEP_LINK_PROTOCOL } from '../../../../constants'
import { trackProductCTA } from '../../../../tracking'
import styles from './Entry.scss'

type Product = {
  product_url?: string
  image_url?: string
  name: string
  unit_price: number
  reference: string
  quantity: number
  total_discount_amount: number
  total_amount: number
}

type EntryProps = {
  product: Product
  returned?: boolean
  isTargetApp?: boolean
}

const Entry: React.FC<EntryProps> = ({
  product: {
    product_url,
    image_url,
    name,
    unit_price,
    reference,
    quantity,
    total_discount_amount,
    total_amount,
  },
  returned = false,
  isTargetApp = false,
}) => {
  const getProductDetailPageURL = (): string | null => {
    if (product_url) {
      if (isTargetApp) {
        const { pathname, hostname } = new URL(product_url)
        return `${APP_DEEP_LINK_PROTOCOL}://${hostname}${pathname}`
      }
      return product_url
    }
    return null
  }

  const productPageURL = getProductDetailPageURL()

  return (
    <li className={styles.container}>
      <MediaContainer>
        {image_url && (
          <Media>
            {productPageURL ? (
              <a
                className={styles.linkImageContainer}
                href={productPageURL}
                onClick={() => trackProductCTA(reference)}
              >
                <Image url={image_url} altText={name} className={cn(styles.image)} />
              </a>
            ) : (
              <Image url={image_url} altText={name} className={cn(styles.image)} />
            )}
          </Media>
        )}
        <Content>
          <BodyText className={styles.productInfo}>
            {!image_url && productPageURL ? (
              <CTALink
                className={styles.productInfoLink}
                href={productPageURL}
                onClick={() => trackProductCTA(reference)}
              >
                {name}
              </CTALink>
            ) : (
              name
            )}
          </BodyText>
          <BodyText className={cn(styles.productInfo, returned && styles.returned)}>
            {formatCurrency(unit_price / 100)}
          </BodyText>
          {returned && (
            <StatusText type="returned" icon={returned} className={styles.productInfo}>
              <Label copyKey="omnicredit.postpurchase.returned" />
            </StatusText>
          )}
          <Table>
            <tbody>
              <tr>
                <MetaText Tag="td">
                  <Label copyKey="order.confirmation.article" />
                </MetaText>
                <MetaText Tag="td">{reference}</MetaText>
              </tr>
              <tr>
                <MetaText Tag="td">
                  <Label copyKey="order.confirmation.quantity" />
                </MetaText>
                <MetaText Tag="td">{quantity}</MetaText>
              </tr>
              {!!total_discount_amount && (
                <tr>
                  <MetaText Tag="td">
                    <Label copyKey="order.confirmation.total.discount" />
                  </MetaText>
                  <MetaText Tag="td" className={cn(returned && styles.returned)}>
                    {formatCurrency(-total_discount_amount / 100)}
                  </MetaText>
                </tr>
              )}
              <tr>
                <MetaText Tag="td">
                  <Label copyKey="order.confirmation.product.total" />
                </MetaText>
                <MetaText Tag="td" className={cn(returned && styles.returned)}>
                  {formatCurrency(total_amount / 100)}
                </MetaText>
              </tr>
            </tbody>
          </Table>
        </Content>
      </MediaContainer>
    </li>
  )
}

export default Entry
